/* eslint-disable indent */
import { IMedia } from "../../../model/unityObject";
import { ContentType, isSceneContentType } from "./dataTypes/ContentType";
import { ContentFieldSchemaEntry, FieldDescriptor, FieldWidget } from "./FieldDescriptor";

export type FieldType = Field["type"];
export type MediaType = MediaField["mediaType"];

export type Field =
  | ScalarField
  | MediaField
  | LongTextField
  | ArrayOfObjectsField
  | NumberFieldT
  | SelectFieldT
  | ColorFieldT;
export type ScalarField = TextField | BooleanField | NumberFieldT | SelectFieldT | ColorFieldT;

type TextField = {
  type: "text";
  fieldName: string;
  currentValue?: string;
  descriptor?: FieldDescriptor;
};
type NumberFieldT = {
  type: "number";
  fieldName: string;
  currentValue?: number | string;
  descriptor?: FieldDescriptor;
};
type SelectFieldT = {
  type: "select";
  fieldName: string;
  currentValue?: string;
  descriptor?: FieldDescriptor;
};
type ColorFieldT = {
  type: "color";
  fieldName: string;
  currentValue?: string;
  descriptor?: FieldDescriptor;
};
type LongTextField = {
  type: "longtext";
  fieldName: string;
  currentValue?: string;
  descriptor?: FieldDescriptor;
};
export type ArrayOfObjectsField = {
  type: "arrayOfObjects";
  fieldName: string;
  currentValue?: any[];
  descriptor?: FieldDescriptor;
};
export type MediaField = {
  type: "media";
  mediaType: "video" | "image" | "audio" | "pdf" | "model3d" | "avatar";
  fieldName: string;
  currentValue?: number | IMedia;
  descriptor?: FieldDescriptor;
};
type BooleanField = {
  type: "boolean";
  fieldName: string;
  currentValue?: boolean;
  descriptor?: FieldDescriptor;
};

export const isScalarField = (field: Field): field is ScalarField =>
  field.type === "text" ||
  field.type === "boolean" ||
  field.type === "number" ||
  field.type === "select" ||
  field.type === "color";
export const isMediaField = (field: Field): field is MediaField => field.type === "media";

const inferMediaTypeFromKey = (key: string): MediaField["mediaType"] => {
  if (key.includes("audio")) return "audio";
  // win/loose answer sounds (source_media_win_sound / source_media_loose_sound)
  // are audio relations — their PLD keys are media_audio_*. Without this the key
  // falls through to the "image" default, which lets the gallery offer images
  // for an audio-only slot. Every *_sound content key maps to media_audio_*.
  if (key.includes("sound")) return "audio";
  if (key.includes("model3d")) return "model3d";
  if (key.includes("pdf")) return "pdf";
  if (key.includes("video")) return "video";
  if (key.includes("avatar")) return "avatar";
  return "image";
};

const mapWidgetToFieldType = (widget: FieldWidget): Field["type"] => {
  switch (widget) {
    case "text":
      return "text";
    case "longtext":
      return "longtext";
    case "boolean":
      return "boolean";
    case "arrayOfObjects":
      return "arrayOfObjects";
    case "media":
      return "media";
    case "number":
      return "number";
    case "select":
      return "select";
    case "color":
      return "color";
  }
};

const readCurrentValue = (
  descriptor: FieldDescriptor,
  contentType: ContentType,
  relevantPld: any,
): any => {
  if (descriptor.pldKey && relevantPld && relevantPld[descriptor.pldKey] !== undefined) {
    return relevantPld[descriptor.pldKey];
  }
  return (contentType.object as any)?.[descriptor.key];
};

export const extractFieldsFromSchema = (
  contentType: ContentType,
  schema: ContentFieldSchemaEntry,
  relevantPld: any,
): Field[] => {
  return schema.fields.map((descriptor): Field => {
    const currentValue = readCurrentValue(descriptor, contentType, relevantPld);
    const type = mapWidgetToFieldType(descriptor.widget);
    if (type === "media") {
      return {
        type: "media",
        mediaType: inferMediaTypeFromKey(descriptor.key),
        fieldName: descriptor.key,
        currentValue,
        descriptor,
      };
    }
    if (type === "boolean") {
      return {
        type: "boolean",
        fieldName: descriptor.key,
        currentValue: !!currentValue,
        descriptor,
      };
    }
    if (type === "longtext") {
      return {
        type: "longtext",
        fieldName: descriptor.key,
        currentValue,
        descriptor,
      };
    }
    if (type === "arrayOfObjects") {
      return {
        type: "arrayOfObjects",
        fieldName: descriptor.key,
        currentValue: Array.isArray(currentValue) ? currentValue : [],
        descriptor,
      };
    }
    if (type === "number") {
      return {
        type: "number",
        fieldName: descriptor.key,
        currentValue,
        descriptor,
      };
    }
    if (type === "select") {
      return {
        type: "select",
        fieldName: descriptor.key,
        currentValue,
        descriptor,
      };
    }
    if (type === "color") {
      return {
        type: "color",
        fieldName: descriptor.key,
        currentValue,
        descriptor,
      };
    }
    return {
      type: "text",
      fieldName: descriptor.key,
      currentValue,
      descriptor,
    };
  });
};

/**
 * Build a descriptor-bearing scalar text field for the legacy (non-slopify)
 * extractor path. Carrying a descriptor with the correct `pldKey` lets a
 * translatable column read PLD-first (per active language) exactly like the
 * schema path — so editing a translated language shows/saves that language's
 * text instead of clobbering it with the source-language value. Fields with a
 * null `pldKey` are source-only scalars (saved to the content table).
 */
const makeScalarField = (
  key: string,
  widget: "text" | "longtext",
  pldKey: string | null,
  contentType: ContentType,
  relevantPld: any,
): Field => {
  const descriptor: FieldDescriptor = {
    key,
    widget,
    jsonSchema: { type: "string" },
    description: null,
    required: false,
    pldKey,
    isTranslatable: !!pldKey,
  };
  return {
    type: widget,
    fieldName: key,
    currentValue: readCurrentValue(descriptor, contentType, relevantPld),
    descriptor,
  };
};

export const extractFieldsFromContentType = (
  contentType: ContentType,
  relevantPld?: any,
): Field[] => {
  // the result array
  // there is always the name field to validate.
  // 48-char descriptor mirrors what buildUnifiedSchema.js sets for
  // slopify-supported types (the in-VR creator's own inputs cap at 24).
  const fields: Field[] = [
    {
      type: "text",
      fieldName: "name",
      currentValue: contentType.object.name,
      descriptor: {
        key: "name",
        widget: "text",
        jsonSchema: { type: "string", maxLength: 48 },
        description: null,
        required: true,
        pldKey: null,
        isTranslatable: false,
      },
    },
  ];

  if (isSceneContentType(contentType)) {
    fields.push({
      type: "media",
      mediaType: "image",
      fieldName: "source_media",
      currentValue: contentType.object.source_media,
    });
    fields.push({
      type: "media",
      mediaType: "model3d",
      fieldName: "source_model3d",
      currentValue: contentType.object.source_model3d,
    });
  } else {
    switch (contentType.modelName) {
      case "content-audio":
        fields.push({
          type: "media",
          mediaType: "audio",
          fieldName: "source_audio",
          currentValue: contentType.object.source_audio,
        });
        break;
      case "content-onboarding":
        fields.push({
          type: "media",
          mediaType: "image",
          fieldName: "source_image_2d",
          currentValue: contentType.object.source_image_2d,
        });
        break;
      case "content-thumbnail":
        fields.push(
          makeScalarField("text", "text", "text", contentType, relevantPld),
          makeScalarField("gazed_text", "text", "gazed_text", contentType, relevantPld),
        );
        fields.push({
          type: "media",
          mediaType: "image",
          fieldName: "source_image",
          currentValue: contentType.object.source_image,
        });
        break;
      case "content-quizz-2":
        fields.push(
          {
            type: "media",
            mediaType: "image",
            fieldName: "answer_1_image",
            currentValue: contentType.object.answer_1_image,
          },
          {
            type: "media",
            mediaType: "image",
            fieldName: "answer_2_image",
            currentValue: contentType.object.answer_2_image,
          },
          {
            type: "media",
            mediaType: "image",
            fieldName: "answer_3_image",
            currentValue: contentType.object.answer_3_image,
          },
        );
        break;
      // content-tuto-2 shares content-tuto's single editable media (its logo).
      // The live modelName is "content-tuto-2"; "content-tuto2" is kept as a
      // defensive alias (both were previously name-only).
      case "content-tuto":
      case "content-tuto-2":
      case "content-tuto2":
        fields.push({
          type: "media",
          mediaType: "image",
          fieldName: "source_logo",
          currentValue: (contentType.object as any).source_logo,
        });
        break;
      case "content-video-2-d":
        fields.push({
          type: "media",
          mediaType: "video",
          fieldName: "source_media",
          currentValue: contentType.object.source_media,
        });
        break;
      case "content-pdf-viewer":
        fields.push({
          type: "media",
          mediaType: "pdf",
          fieldName: "source_pdf",
          currentValue: contentType.object.source_pdf,
        });
        break;
      case "content-3d-viewer":
      case "content-3-d-viewer":
        fields.push({
          type: "media",
          mediaType: "model3d",
          fieldName: "source_model3d",
          currentValue: contentType.object.source_model3d,
        });
        break;
      case "content-avatar-audio-player":
        fields.push({
          type: "media",
          mediaType: "audio",
          fieldName: "source_audio",
          currentValue: contentType.object.source_audio,
        });
        fields.push({
          type: "media",
          mediaType: "avatar",
          fieldName: "source_avatar",
          currentValue: contentType.object.source_avatar,
        });
        break;
      case "content-danger-catch":
        // Media fields resolve via the content-danger-catches PLD pairs, which
        // only surface once the Phase 1.1 pluralization fix is in place.
        fields.push(
          makeScalarField("text_success", "text", "text_success", contentType, relevantPld),
          makeScalarField("text_fail", "text", "text_fail", contentType, relevantPld),
          {
            type: "media",
            mediaType: "image",
            fieldName: "image_success",
            currentValue: (contentType.object as any).image_success,
          },
          {
            type: "media",
            mediaType: "image",
            fieldName: "image_fail",
            currentValue: (contentType.object as any).image_fail,
          },
          {
            type: "media",
            mediaType: "audio",
            fieldName: "audio_catch",
            currentValue: (contentType.object as any).audio_catch,
          },
        );
        break;
      case "content-fire-extinguisher":
        // Setters are heavily repurposed (§0.3); the DB columns below are the
        // source of truth and each maps to a fire-extinguisher PLD pair.
        fields.push(
          makeScalarField("title", "text", "title", contentType, relevantPld),
          makeScalarField("text_instructions", "longtext", "text", contentType, relevantPld),
          makeScalarField("title_win", "text", "explanation_true", contentType, relevantPld),
          makeScalarField("title_loose", "text", "explanation_false", contentType, relevantPld),
          makeScalarField("text_win", "longtext", "end_text", contentType, relevantPld),
          makeScalarField("text_loose", "longtext", "text_fail", contentType, relevantPld),
          makeScalarField(
            "instructions_panel_text",
            "longtext",
            "intro_text",
            contentType,
            relevantPld,
          ),
        );
        break;
      case "content-zone-sort":
        fields.push(
          makeScalarField("title", "text", "title", contentType, relevantPld),
          makeScalarField("entitled", "text", "text", contentType, relevantPld),
          {
            type: "boolean",
            fieldName: "has_to_check_answers",
            currentValue: !!(contentType.object as any).has_to_check_answers,
          },
          {
            // hotspot_info is edited by a bespoke constrained editor
            // (HotspotInfoField, §2.D pt 4); the array is seeded into RHF by the
            // NodeForm mount effect. Carrying the PLD key lets the seeding
            // overlay the active language's explanation text.
            type: "arrayOfObjects",
            fieldName: "hotspot_info",
            currentValue: Array.isArray((contentType.object as any).hotspot_info)
              ? (contentType.object as any).hotspot_info
              : [],
            descriptor: {
              key: "hotspot_info",
              widget: "arrayOfObjects",
              jsonSchema: { type: "array" },
              description: null,
              required: false,
              pldKey: "list_hotspot_zone_sort_data",
              isTranslatable: true,
            },
          },
        );
        break;
      case "content-clean":
        fields.push(
          makeScalarField("title", "text", "title", contentType, relevantPld),
          makeScalarField("entitled", "text", "text", contentType, relevantPld),
          {
            type: "media",
            mediaType: "image",
            fieldName: "default_image_1",
            currentValue: (contentType.object as any).default_image_1,
          },
          {
            type: "media",
            mediaType: "image",
            fieldName: "default_image_2",
            currentValue: (contentType.object as any).default_image_2,
          },
        );
        break;
      case "content-photo-catch":
        fields.push(
          makeScalarField("text_instruction", "longtext", "text", contentType, relevantPld),
          makeScalarField("text_success", "text", "text_success", contentType, relevantPld),
          makeScalarField("text_fail", "text", "text_fail", contentType, relevantPld),
        );
        break;
      case "content-hotspot-scene-image":
      case "content-hotspot-scene-video":
        fields.push(makeScalarField("text", "text", "text", contentType, relevantPld));
        break;
      case "content-scoring-scene":
        // Previously rendered as `[]` (not even name). Now exposes name + the
        // two VR-editable Core fields. Neither has a PLD pair — source-only
        // scalars (pldKey null); Phase 4 adds the non-source-language gating.
        fields.push(
          {
            type: "number",
            fieldName: "multiplier",
            currentValue: (contentType.object as any).multiplier,
            descriptor: {
              key: "multiplier",
              widget: "number",
              jsonSchema: { type: "number", minimum: 1, maximum: 50 },
              description: null,
              required: false,
              pldKey: null,
              isTranslatable: false,
              displayMode: "decimal",
            },
          },
          {
            type: "select",
            fieldName: "icon_type",
            currentValue: (contentType.object as any).icon_type,
            descriptor: {
              key: "icon_type",
              widget: "select",
              jsonSchema: { type: "string" },
              description: null,
              required: false,
              pldKey: null,
              isTranslatable: false,
              // Curated option list (icon_type is a free string, not a backend
              // enum): the three VR radio choices (Appendix C).
              options: [
                { value: "point", optionText: "Point" },
                { value: "star", optionText: "Star" },
                { value: "clock", optionText: "Clock" },
              ],
            },
          },
        );
        break;
      case "content-speech-to-check":
      case "content-quizz-1":
      case "content-text":
      case "content-zone-detect":
      case "content-quizz-3":
      case "content-yes-no-question":
      case "content-element-catch":
      case "content-highlight":
      case "content-quizz-sort":
      case "content-fire":
      case "content-dust":
      case "content-quizz-image-multiple":
      case "content-avatar-ai":
      case "content-avatar-ai-persona":
        break;
      /*
      case "content-button-1":
      case "content-button-2":
      case "content-scoring-general":
      case "content-vr-locator":
        break; // only the name field is of interest
       */
    }
  }

  return fields;
};
